export interface CatalogActivityCounts {
  productsProcessed?: number;
  productsCreated?: number;
  productsUpdated?: number;
}

const count = (value: unknown): number => Math.max(0, Math.floor(Number(value) || 0));

/**
 * Last-run catalog line for Priya. `productsProcessed` is "touched this run",
 * not "new to the catalog" — a successful weekly ProMaster refresh of an
 * already-full catalog often has 0 created and must not read as "the feed
 * ingested nothing".
 */
export function formatCatalogActivityText(
  job: CatalogActivityCounts,
  connectorName?: string | null,
  catalogTotal?: number | null
): string {
  const created = count(job.productsCreated);
  const updated = count(job.productsUpdated);
  const processed = count(job.productsProcessed);
  const catalog = count(catalogTotal);
  const feed = connectorName?.trim() || 'your feed';
  const catalogLabel = catalog > 0 ? `${catalog.toLocaleString()} in catalog` : null;

  if (created > 0) {
    const base = `${created.toLocaleString()} new products ingested from ${feed}`;
    return catalogLabel ? `${base} · ${catalogLabel}` : base;
  }

  if (processed > 0 || updated > 0) {
    const touched = processed > 0 ? processed : updated;
    const base = `${touched.toLocaleString()} products refreshed, no new from ${feed}`;
    return catalogLabel ? `${base} · ${catalogLabel}` : base;
  }

  if (catalog > 0) {
    return `No new products this run from ${feed} · ${catalog.toLocaleString()} already in catalog`;
  }

  return `0 products ingested from ${feed}`;
}

export function formatSyncHistoryProductLabel(
  job: CatalogActivityCounts,
  catalogTotal?: number | null
): string {
  const created = count(job.productsCreated);
  const updated = count(job.productsUpdated);
  const processed = count(job.productsProcessed);
  const catalog = count(catalogTotal);

  if (created > 0 && (processed > created || updated > 0)) {
    return `${created.toLocaleString()} new · ${Math.max(processed - created, updated).toLocaleString()} refreshed`;
  }
  if (created > 0) {
    return `${created.toLocaleString()} new`;
  }
  if (processed > 0 || updated > 0) {
    const touched = processed > 0 ? processed : updated;
    return `${touched.toLocaleString()} refreshed · no new`;
  }
  if (catalog > 0) {
    return `No new · ${catalog.toLocaleString()} in catalog`;
  }
  return '0 products';
}
